import React, { useState } from "react";
import { Send, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { submitSurvey } from "../lib/api";

const Field = ({ label, name, value, onChange, type = "text", placeholder, required, textarea, testId }) => (
  <div className="space-y-2">
    <label htmlFor={name} className="block text-sm font-semibold text-[#2a1f4a]">
      {label} {required && <span className="text-[#ecac1f]">*</span>}
    </label>
    {textarea ? (
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        rows={4}
        placeholder={placeholder}
        className="brand-input w-full px-4 py-3 text-base text-[#2a1f4a] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors resize-none"
        data-testid={testId}
      />
    ) : (
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="brand-input w-full px-4 py-3 text-base text-[#2a1f4a] bg-white border-2 border-slate-200 rounded-xl focus:border-[#63499d] focus:outline-none transition-colors"
        data-testid={testId}
      />
    )}
  </div>
);

export const QuoteForm = ({ answers, recommendation, onBack, onSuccess }) => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service_area: "",
    fleet_size: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.company.trim()) return "Please enter your company.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (form.phone.replace(/\D/g, "").length < 7) return "Please enter a valid phone number.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitSurvey({ contact: form, answers });
      toast.success("Your quote request is in. Ricochet will be in touch shortly.");
      onSuccess(result);
    } catch (e2) {
      console.error(e2);
      const msg = e2?.response?.data?.detail || "Something went wrong submitting your request.";
      toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 rc-fade-up"
      data-testid="quote-form-section"
    >
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="gold-bar" />
          <span className="text-[#63499d] text-xs font-bold tracking-[0.18em] uppercase">
            Final Step
          </span>
        </div>
        <h2
          className="font-display text-3xl sm:text-4xl text-[#2a1f4a] leading-tight"
          data-testid="quote-form-title"
        >
          Get a free quote
        </h2>
        <p className="text-sm sm:text-base text-slate-500 mt-3">
          We'll review your answers plus your recommended solution
          {recommendation?.primary?.name && (
            <>
              {" "}— <span className="font-semibold text-[#63499d]">{recommendation.primary.name}</span>
              {recommendation.is_hybrid && (
                <> + <span className="font-semibold text-[#63499d]">{recommendation.alternate.name}</span></>
              )}
            </>
          )}{" "}
          — and send back a custom proposal.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-[#63499d]/10 p-6 sm:p-10 space-y-5"
        data-testid="quote-form"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field
            label="Your Name"
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="Jane Operator"
            required
            testId="quote-input-name"
          />
          <Field
            label="Company"
            name="company"
            value={form.company}
            onChange={onChange}
            placeholder="Acme Logistics"
            required
            testId="quote-input-company"
          />
          <Field
            label="Email"
            name="email"
            value={form.email}
            onChange={onChange}
            type="email"
            placeholder="jane@acmelogistics.com"
            required
            testId="quote-input-email"
          />
          <Field
            label="Phone"
            name="phone"
            value={form.phone}
            onChange={onChange}
            type="tel"
            placeholder="(555) 555-1234"
            required
            testId="quote-input-phone"
          />
          <Field
            label="Service Area / City, State"
            name="service_area"
            value={form.service_area}
            onChange={onChange}
            placeholder="Dallas, TX"
            testId="quote-input-service-area"
          />
          <Field
            label="Fleet / Equipment Size"
            name="fleet_size"
            value={form.fleet_size}
            onChange={onChange}
            placeholder="e.g. 25 trucks + 6 generators"
            testId="quote-input-fleet-size"
          />
        </div>

        <Field
          label="Anything else we should know?"
          name="notes"
          value={form.notes}
          onChange={onChange}
          textarea
          placeholder="Schedule constraints, fuel types needed, jobsite locations, etc."
          testId="quote-input-notes"
        />

        <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
          <button
            type="button"
            onClick={onBack}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-[#63499d] hover:text-[#4d3884] disabled:opacity-40 transition-colors"
            data-testid="quote-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to recommendation
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-bold"
            data-testid="quote-submit-button"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                Send my request
                <Send className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        <p className="text-xs text-slate-400 leading-relaxed pt-1">
          By submitting, you agree Ricochet Fuel Distributors may contact you about your request.
          We never sell your information.
        </p>
      </form>
    </section>
  );
};

export default QuoteForm;
